import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Typography,
  Box,
  Chip,
  Divider,
  Paper,
  Collapse,
  Skeleton,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import Timeline from '@mui/lab/Timeline';
import TimelineItem from '@mui/lab/TimelineItem';
import TimelineSeparator from '@mui/lab/TimelineSeparator';
import TimelineDot from '@mui/lab/TimelineDot';
import TimelineConnector from '@mui/lab/TimelineConnector';
import TimelineContent from '@mui/lab/TimelineContent';
import { humanReadableDate, formatCurrency, titleCase, fitnessMembershipStatusColor, FITNESS_PAYMENT_ITEM_TYPE_LABEL } from '../common/AppUtils';


function LoadingSkeleton() {
  return (
    <Box sx={{ p: 1 }}>
      <TimelineCardSkeleton />  {/* Recent subscription card */}
      <TimelineCardSkeleton />  {/* Older subscription card */}
      {/* Rejoined / Joined event */}
      {/* <TimelineEventSkeleton /> */}
      {/* Left event */}
      {/* <TimelineEventSkeleton /> */}
      <TimelineCardSkeleton />  {/* Older subscription card */}
      <TimelineEventSkeleton /> {/* Original Joined event */}
    </Box>
  );
}

function TimelineCardSkeleton() {
  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'flex-start',
        gap: 2,
        mb: 2,
      }}
    >
      <Skeleton variant="circular" width={12} height={12} sx={{ mt: 1 }} />
      <Box sx={{ flex: 1 }}>
        <Skeleton
          variant="rounded"
          width="100%"
          height={110}
          sx={{ borderRadius: 2 }}
        />
      </Box>
    </Box>
  );
}

function TimelineEventSkeleton() {
  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 2,
        mb: 2,
      }}
    >
      <Skeleton variant="circular" width={12} height={12} />
      <Box>
        <Skeleton variant="text" width={70} height={20} />
        <Skeleton variant="text" width={100} height={16} />
      </Box>
    </Box>
  );
}

const pulseAnimationSx = {
  animation: "pulse 1.8s infinite",

  "@keyframes pulse": {
    "0%": {
      boxShadow: "0 0 0 0 rgba(76,175,80,0.7)",
    },
    "70%": {
      boxShadow: "0 0 0 10px rgba(76,175,80,0)",
    },
    "100%": {
      boxShadow: "0 0 0 0 rgba(76,175,80,0)",
    },
  },
};

function LineItemRow({ item }) {
  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        py: 0.5,
        px: 0.5,
        borderRadius: 1,
        // "&:nth-of-type(odd)": { bgcolor: "action.hover" },
      }}
    >

      <Typography variant="caption" color="text.secondary">
        {FITNESS_PAYMENT_ITEM_TYPE_LABEL[item.item_type] ?? item.item_type}
      </Typography>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        {item.discount > 0 && (
          <Typography variant="caption" sx={{ color: "text.disabled", textDecoration: "line-through" }}>
            {formatCurrency(item.price)}
          </Typography>
        )}
        <Typography variant="caption" color="text.secondary">
          {formatCurrency(item.amount)}
        </Typography>
      </Box>
    </Box>
  );
}

function PaymentBlock({ payment }) {
  const [expanded, setExpanded] = useState(false);

  return (
    <Box
      sx={{
        mb: 1,
        p: 1.25,
        bgcolor: 'grey.50',
        borderRadius: 1,
      }}
    >
      {/* Payment summary row */}
      <Box display="flex" justifyContent="space-between" alignItems="flex-start">
        {/* Left */}
        <Box>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Typography fontWeight={600} color="success.main">
              {formatCurrency(payment.amount)}
            </Typography>

            {payment.discount > 0 && (
              <Typography
                variant="caption"
                sx={{
                  color: "text.disabled",
                  textDecoration: "line-through",
                }}
              >
                {formatCurrency(payment.price)}
              </Typography>
            )}
          </Box>

          <Typography variant="body2">
            {humanReadableDate(payment.payment_date)}
          </Typography>

          <Typography variant="body2">
            {titleCase(payment.payment_mode)}
          </Typography>
        </Box>

        {/* Right */}
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "flex-end",
            gap: 1.25,
          }}
        >
          <Chip
            size="small"
            label={titleCase(payment.status)}
            color={
              payment.status === "paid" ? "success" :
              payment.status === "pending" ? "warning" :
              "error"
            }
            sx={{ height: 20, fontSize: 11 }}
          />
          {payment.line_items?.length > 0 && (
            <IconButton
              size="small"
              onClick={() => setExpanded((v) => !v)}
              sx={{ p: 0.25 }}
            >
              {expanded ? (
                <ExpandLessIcon fontSize="small" />
              ) : (
                <ExpandMoreIcon fontSize="small" />
              )}
            </IconButton>
          )}
        </Box>
      </Box>
      {/* Line items */}
      <Collapse in={expanded}>
        <Divider sx={{ my: 1 }} />
        {payment.line_items?.map((item, i) => (
          <LineItemRow key={item.id} item={item} />
        ))}
      </Collapse>
    </Box>
  );
}

export default function MemberJourneyModal({ open, data, loading, onClose }) {

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
    >
      <DialogTitle>
        {data?.full_name ?? 'Member Journey'}
        <IconButton
          onClick={onClose}
          sx={{
            position: 'absolute',
            right: 8,
            top: 8,
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers>
        {loading ? (
          <LoadingSkeleton />
        ) : (
          <Timeline
            position="right"
            sx={{
              p: 0,
              m: 0,
              '& .MuiTimelineItem-root:before': { display: 'none' },
            }}
          >
            {(data?.memberships || []).map((membership) => (
              <Box key={membership.id}>

                {/* Left */}
                {membership.left_at && (
                  <TimelineItem>
                    <TimelineSeparator>
                      <TimelineDot color="error" variant="outlined"/>
                      <TimelineConnector />
                    </TimelineSeparator>
                    <TimelineContent sx={{ pl: 2, pr: 0.5 }}>
                      <Typography variant="h6">Left</Typography>
                      <Typography variant="body2" color="text.secondary">
                        {humanReadableDate(membership.left_at)}
                      </Typography>
                    </TimelineContent>
                  </TimelineItem>
                )}

                {/* Subscriptions */}
                {membership.regular_subscriptions.map((subscription) => (
                  <TimelineItem key={subscription.id}>
                    <TimelineSeparator>
                      <TimelineDot
                        color={fitnessMembershipStatusColor(subscription.status, subscription.date_status, subscription.is_current, false)}
                        variant={subscription.date_status === "current" ? "filled" : "outlined"}
                        sx={subscription.date_status === "current" ? pulseAnimationSx : undefined}
                      />
                      <TimelineConnector />
                    </TimelineSeparator>

                    <TimelineContent sx={{ pl: 2, pr: 0.5 }}>
                      <Paper variant="outlined" sx={{ p: 1.75, borderRadius: 2 }}>

                        {/* Sub header */}
                        <Box display="flex" justifyContent="space-between" alignItems="center" mb={0.5}>
                          <Typography fontWeight={600} color="primary.main">{subscription.plan_name}</Typography>
                          <Chip
                            size="small"
                            label={`${subscription["plan_validity_months"] ?? "-"} M`}
                            variant="outlined"
                            sx={{
                              maxHeight: 18,
                              minWidth: 36,
                              fontSize: '0.8rem',
                              // color: chipColor,
                              // borderColor: chipColor,
                            }}
                          />
                        </Box>

                        <Typography variant="body2" color="text.secondary" mb={0.5}>
                          {humanReadableDate(subscription.start_date)}
                          {' → '}
                          {humanReadableDate(subscription.end_date)}
                        </Typography>

                        {/* Payments */}
                        {subscription.payments?.length > 0 && (
                          <>
                            <Divider sx={{ my: 1.5 }} />
                            {subscription.payments.map((payment) => (
                              <PaymentBlock key={payment.id} payment={payment} />
                            ))}
                          </>
                        )}
                      </Paper>
                    </TimelineContent>
                  </TimelineItem>
                ))}

                {/* Joined */}
                <TimelineItem>
                  <TimelineSeparator>
                    <TimelineDot color='success' variant="outlined" />
                  </TimelineSeparator>
                  <TimelineContent sx={{ pl: 2, pr: 0.5 }}>
                    <Typography variant="h6">Joined</Typography>
                    <Typography variant="body2" color="text.secondary">
                      {humanReadableDate(membership.joined_at)}
                    </Typography>
                    {/* {membership.joining_amount > 0 && (
                      <Typography variant="body2">Joining fee ₹{membership.joining_amount}</Typography>
                    )} */}
                  </TimelineContent>
                </TimelineItem>
              </Box>
            ))}
          </Timeline>
        )}
      </DialogContent>
    </Dialog>
  );
}
